/**
 * useNewFeedbackCount Hook
 *
 * Custom hook for fetching the count of new (unreviewed) feedback items.
 * Used by admin components to display notification badges.
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';

/**
 * State interface for new feedback count query
 */
interface UseNewFeedbackCountState {
  /** Number of new feedback items */
  count: number;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useNewFeedbackCount hook
 */
interface UseNewFeedbackCountResult extends UseNewFeedbackCountState {
  /** Refetch the count */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch the count of new feedback items
 *
 * @returns Object containing count, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { count, loading, error, refetch } = useNewFeedbackCount();
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     {count > 0 && <Badge>{count} new</Badge>}
 *     <button onClick={refetch}>Refresh</button>
 *   </div>
 * );
 * ```
 */
export const useNewFeedbackCount = (): UseNewFeedbackCountResult => {
  const { user, isAdmin } = useAuth();

  const [state, setState] = useState<UseNewFeedbackCountState>({
    count: 0,
    loading: true,
    error: null,
  });

  /**
   * Fetch the count of new feedback items
   */
  const fetchCount = useCallback(async (): Promise<void> => {
    // Skip if not logged in or not admin
    if (!user || !isAdmin) {
      setState({ count: 0, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const feedbackQuery = query(collection(db, 'feedback'), where('status', '==', 'new'));
      const snapshot = await getCountFromServer(feedbackQuery);

      setState({ count: snapshot.data().count, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch feedback count';
      console.error('Error fetching feedback count:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [user, isAdmin]);

  // Fetch count when user and admin status are available
  useEffect(() => {
    let isMounted = true;

    const doFetch = async () => {
      if (!isMounted) return;
      await fetchCount();
    };

    doFetch();

    return () => {
      isMounted = false;
    };
  }, [fetchCount]);

  return {
    ...state,
    refetch: fetchCount,
  };
};
